import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useQuery, useUpdateQuery } from '../../../hooks/useQuery'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PRESS_RELAESES_FILTER_PARAMS, validateContentType, validateEndDate } from './utils'
import { endOfMonth } from 'date-fns'
import Autocomplete from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import { debounce } from '@mui/material/utils'
import { PressReleasesFilterContentType } from '@finance-lab/utils/types'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { TFunction } from 'i18next'

const contentTypes = Object.keys({
    all: true,
    analyzeAndIr: true,
    pressReleases: true,
    reports: true,
    relatedTransactions: true,
}) as PressReleasesFilterContentType[]

const getContentTypeLabel = (type: PressReleasesFilterContentType, t: TFunction) =>
    t(`components.pressReleasesFilter.contentType.${type}`)

export const PressReleasesFilter = ({
    hasContentTypeSelector = true,
    isSlim,
    isSingleDaySelect,
}: {
    hasContentTypeSelector?: boolean
    isSingleDaySelect?: boolean
    isSlim?: boolean
}) => {
    const { t } = useTranslation()
    const query = useQuery()
    const updateQuery = useUpdateQuery()
    const [isOpen, setIsOpen] = useState(false)
    const contentType = validateContentType(query.get(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE))
    const endDate = validateEndDate(query.get(PRESS_RELAESES_FILTER_PARAMS.END_DATE))
    const searchFromUrl = query.get(PRESS_RELAESES_FILTER_PARAMS.SEARCH) ?? ''
    const [searchInput, setSearchInput] = useState(searchFromUrl)

    useEffect(() => {
        setSearchInput(searchFromUrl)
    }, [searchFromUrl])

    const handleChangeEndDate = (value: Date | null) => {
        const formatted = formatDateYYYYMMDD(value, true)
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.END_DATE, formatted)
    }

    const handleChangeSearchValue = useMemo(
        () =>
            debounce((value: string) => {
                updateQuery(PRESS_RELAESES_FILTER_PARAMS.SEARCH, value || null)
            }, 300),
        [updateQuery],
    )

    useEffect(() => () => handleChangeSearchValue.clear(), [handleChangeSearchValue])

    const handleChangeContentType = (value: PressReleasesFilterContentType) => {
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE, value)
    }

    return (
        <Grid container spacing={2} direction={hasContentTypeSelector ? 'row' : 'row-reverse'}>
            {hasContentTypeSelector && (
                <Grid size={6}>
                    <Box sx={{ marginY: 2 }}>
                        <FormControl fullWidth>
                            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                                <Autocomplete<PressReleasesFilterContentType, true, true>
                                    disableClearable
                                    options={contentTypes}
                                    getOptionLabel={(v) => getContentTypeLabel(v, t)}
                                    limitTags={1}
                                    open={isOpen}
                                    onClose={() => setIsOpen(false)}
                                    onOpen={() => setIsOpen(true)}
                                    renderValue={(value) => {
                                        const numTags = value.length

                                        if (value.length > 1)
                                            return t('components.pressReleasesFilter.labels.twoSelected')

                                        return (
                                            <Typography variant='body2'>
                                                {value
                                                    .slice(0, 1)
                                                    .map((option) => getContentTypeLabel(option, t))
                                                    .join(', ')}

                                                {numTags > 1 && ` +${numTags - 1}`}
                                            </Typography>
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{}}
                                            label={t('components.pressReleasesFilter.labels.content')}
                                            onClick={() => setIsOpen(true)}
                                        />
                                    )}
                                    onChange={(e, v) => handleChangeContentType(v)}
                                    size={isSlim ? 'small' : undefined}
                                    sx={{
                                        width: '100%',
                                        '.MuiInputBase-root': { cursor: 'pointer' },
                                        '.MuiInputBase-input': { visibility: 'hidden' },
                                    }}
                                    value={[contentType]}
                                />
                            </ClickAwayListener>
                        </FormControl>
                    </Box>
                </Grid>
            )}

            <Grid size={6}>
                <Box sx={{ marginY: 2 }}>
                    <DatePicker
                        label={
                            isSingleDaySelect
                                ? t('components.pressReleasesFilter.labels.pointInTime')
                                : t('site.dates.periodEnd')
                        }
                        views={isSingleDaySelect ? ['year', 'month', 'day'] : ['year', 'month']}
                        format={'dd MMM yy'}
                        minDate={new Date('2015-01-01')}
                        maxDate={new Date()}
                        onChange={(newValue) => {
                            const d = newValue ?? new Date()
                            const newEndDate = isSingleDaySelect ? d : endOfMonth(d)

                            handleChangeEndDate(new Date(Math.min(new Date().getTime(), newEndDate.getTime())))
                        }}
                        slotProps={{
                            shortcuts: { items: [{ label: 'Idag', getValue: () => new Date() }] },
                            textField: {
                                size: isSlim ? 'small' : undefined,
                            },
                        }}
                        value={new Date(endDate)}
                    />
                </Box>
            </Grid>

            <Grid size={hasContentTypeSelector ? 12 : 6}>
                <Box>
                    <TextField
                        label={t('components.pressReleasesFilter.labels.freeTextSearch')}
                        size={isSlim ? 'small' : undefined}
                        value={searchInput}
                        onChange={(event) => {
                            const value = event.target.value
                            setSearchInput(value)
                            handleChangeSearchValue(value)
                        }}
                    />
                </Box>
            </Grid>
        </Grid>
    )
}
